import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getCheckImageUrl } from '../../api/transactions';
import styles from './CheckImageLightbox.module.css';

/**
 * Modal that shows the native-resolution check / deposit-slip crop. Two sources:
 * a matched transaction (pass `txnID` — fetched via a short-lived presigned URL
 * keyed by transaction id, #6), or an already-presigned `imageUrl` (an UNMATCHED
 * crop the HIL agent presigned by crop reference, which has no transaction yet).
 * The S3 key never reaches the client either way. Uses the themed MUI Dialog to
 * stay consistent with the other transaction dialogs.
 */
export default function CheckImageLightbox({ open, onClose, txnID, imageUrl, docKind }) {
    const [state, setState] = useState({ loading: false, url: null, error: null });
    const label = docKind === 'deposit_slip' ? 'Deposit Slip Image' : 'Check Image';

    useEffect(() => {
        if (!open) return undefined;
        // Direct URL (an unmatched crop the agent already presigned) — nothing to fetch.
        if (imageUrl) {
            setState({ loading: false, url: imageUrl, error: null });
            return undefined;
        }
        if (!txnID) return undefined;
        let cancelled = false;
        setState({ loading: true, url: null, error: null });
        getCheckImageUrl(txnID)
            .then((res) => {
                if (cancelled) return;
                if (res && res.url) {
                    setState({ loading: false, url: res.url, error: null });
                } else {
                    setState({ loading: false, url: null, error: 'No image is available for this transaction.' });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setState({ loading: false, url: null, error: 'Could not load the image. Please try again.' });
                }
            });
        return () => { cancelled = true; };
    }, [open, txnID, imageUrl]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-label={label}>
            <DialogTitle className={styles.header}>
                <span className={styles.title}>{label}</span>
                <IconButton onClick={onClose} aria-label="Close image" size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <div className={styles.body}>
                    {state.loading && <Typography className={styles.message}>Loading&hellip;</Typography>}
                    {state.error && <Typography className={styles.message}>{state.error}</Typography>}
                    {state.url && <img className={styles.image} src={state.url} alt={label} />}
                </div>
            </DialogContent>
        </Dialog>
    );
}
